// AWS costs (SCR-012, FLOW-007): where the AWS cost behind profit comes from. The fleet default
// cost basis; for a chosen customer its own basis, its Cost and Usage Report (CUR) sources and a
// jump to one of its months (finalize.tsx); and the CUR usage-type map, which is global.
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import type { ApiClient } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import type { ScreenProps } from "../../router/route";
import { buildPath, RouterContext } from "../../router/router";
import { SessionContext } from "../../session/session";
import { CustomerPicker, useCustomerDirectory, useCustomerName } from "../../ui/customer-directory";
import { customerName } from "../../ui/customers";
import { errorId, Field, LabelText } from "@nexus/ui";
import { MonthPicker } from "@nexus/ui";
import { addMonths, currentMonth, monthLabel } from "@nexus/ui";
import { Card, Chip, PageHeader, Segmented } from "@nexus/ui";
import { ErrorBanner } from "@nexus/ui";
import { DataTable, type Column } from "@nexus/ui";
import { CurSourcesSection } from "./cur-sources";
import { UsageTypesSection } from "./usage-types";
import {
  EXPENSE_PERIOD_PATH,
  FLEET_BASIS_OPTIONS,
  ORG_BASIS_OPTIONS,
  basisInForceLabel,
  basisRows,
  defaultEffectiveMonth,
  effectiveBasis,
  hasVerifiedCurSource,
  orgBasisValue,
  validateSetBasis,
  type BasisRowView,
  type CurSourceRowView,
  type ExpenseBasis,
  type ExpenseBasisKind,
  type OrgBasisChoice,
} from "./model";

function asApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;
  throw error;
}

export function Expense(_props: ScreenProps<"/aws-costs">) {
  const client = useApi();
  const services = useContext(SessionContext);
  const router = useContext(RouterContext);
  const session = services?.state.status === "signed-in" ? services.state.session : null;
  const canApprove = session?.can("billing:approve") ?? false;
  const canWrite = session?.can("billing:write") ?? false;

  const [orgId, setOrgId] = useState("");

  return (
    <div class="page expense-screen">
      <PageHeader
        title="AWS costs"
        lede="Where the AWS cost behind profit comes from: the AWS list price, or the actual AWS bill from each customer's Cost and Usage Report (CUR)."
      />

      <FleetBasisCard client={client} />

      <Card title="A customer's AWS costs">
        <p class="card-note">Pick a customer to see its own cost basis, its CUR sources and its months.</p>
        <Field id="expense-customer" label="Customer" help="common.customer" class="field-medium">
          <CustomerPicker id="expense-customer" value={orgId} onChange={setOrgId} />
        </Field>
      </Card>

      {orgId && <CustomerCosts key={orgId} client={client} orgId={orgId} canWrite={canWrite} onOpenMonth={(period) => router?.navigate(buildPath(EXPENSE_PERIOD_PATH, { orgId, period }))} />}

      <UsageTypesSection client={client} canApprove={canApprove} />
    </div>
  );
}

function FleetBasisCard({ client }: { client: ApiClient }) {
  const { entries } = useCustomerDirectory();
  const [basis, setBasis] = useState<ExpenseBasis | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => defaultEffectiveMonth(new Date()));
  const [kind, setKind] = useState<ExpenseBasisKind>("list");
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);
  const [withoutSource, setWithoutSource] = useState<readonly string[]>([]);

  const load = () => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/admin/expense-basis")
      .then(setBasis)
      .catch((err: unknown) => setError(asApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const fieldErrors = validateSetBasis({ effectiveMonth: month, basis: kind }, true);
  const submit = (event: Event) => {
    event.preventDefault();
    if (Object.keys(fieldErrors).length > 0) return;
    setBusy(true);
    setSaveError(null);
    client
      .put("/v1/admin/expense-basis", { body: { basis: kind, effectiveMonth: month } })
      .then((result) => {
        setWithoutSource(result.orgsWithoutCurSource ?? []);
        load();
      })
      .catch((err: unknown) => setSaveError(asApiError(err)))
      .finally(() => setBusy(false));
  };

  const now = new Date();
  const inForce = basis ? effectiveBasis(basis, currentMonth(now))?.effectiveMonth ?? null : null;
  const inForceLabel = basis ? basisInForceLabel(basis, now) : null;

  return (
    <Card title="Fleet default cost basis" help="finance.fleetBasis">
      <p class="card-note">
        Every customer follows this unless it has its own. In force now: <strong>{inForceLabel ?? "AWS list price"}</strong>.
      </p>
      {error && <ErrorBanner error={error} onRetry={load} />}
      <div class="split-even">
        <DataTable caption="Fleet default cost basis by month" hideCaption columns={basisColumns(inForce)} rows={basis ? basisRows(basis) : []} rowKey={(row) => row.effectiveMonth} loading={loading} emptyMessage="No cost basis set yet: AWS list price applies." />
        <form class="stack-sm basis-form" onSubmit={submit}>
          <h3>Change the fleet default</h3>
          <Field id="fleet-basis-month" label="Effective from" help="finance.effectiveFrom" error={fieldErrors.effectiveMonth}>
            <MonthPicker id="fleet-basis-month" value={month} onChange={setMonth} min={addMonths(currentMonth(now), -35)} />
          </Field>
          <div class="field">
            <LabelText id="fleet-basis-kind-label" label="Cost basis" help="common.costBasis" />
            <Segmented options={FLEET_BASIS_OPTIONS} value={kind} onChange={(next: ExpenseBasisKind) => setKind(next)} labelledBy="fleet-basis-kind-label" />
          </div>
          {saveError && <ErrorBanner error={saveError} />}
          {withoutSource.length > 0 && (
            <div class="banner banner-warning" role="status">
              <p>
                These customers follow the fleet default but have no verified CUR source yet, so they have no actual AWS bill to read:{" "}
                {withoutSource.map((id) => customerName(entries, id)).join(", ")}.
              </p>
            </div>
          )}
          <div class="form-actions">
            <button type="submit" class="button button-primary" disabled={busy}>
              {busy ? "Saving…" : "Set fleet default"}
            </button>
          </div>
        </form>
      </div>
    </Card>
  );
}

function basisColumns(inForce: string | null): Column<BasisRowView>[] {
  return [
    {
      key: "month",
      header: "Effective from",
      help: "finance.effectiveFrom",
      cell: (row) => (
        <>
          {monthLabel(row.effectiveMonth)}
          {row.effectiveMonth === inForce && (
            <>
              {" "}
              <Chip tone="green">In force</Chip>
            </>
          )}
          {row.effectiveMonth > currentMonth(new Date()) && (
            <>
              {" "}
              <Chip tone="blue">Scheduled</Chip>
            </>
          )}
        </>
      ),
    },
    { key: "basis", header: "Cost basis", help: "common.costBasis", cell: (row) => row.label },
  ];
}

function CustomerCosts({ client, orgId, canWrite, onOpenMonth }: { client: ApiClient; orgId: string; canWrite: boolean; onOpenMonth(period: string): void }) {
  const name = useCustomerName(orgId);
  const [sources, setSources] = useState<readonly CurSourceRowView[]>([]);
  const [period, setPeriod] = useState(() => addMonths(currentMonth(new Date()), -1));
  return (
    <>
      <CustomerBasisCard client={client} orgId={orgId} name={name} hasVerifiedSource={hasVerifiedCurSource(sources)} />
      <CurSourcesSection client={client} orgId={orgId} name={name} canWrite={canWrite} onRows={setSources} />
      <Card title="Reconcile and finalize a month" help="finance.reconcileMonth">
        <p class="card-note">Compare {name}'s actual AWS bill with its metered usage for one month, then freeze the month's AWS cost.</p>
        <form
          class="row reconcile-form"
          onSubmit={(event) => {
            event.preventDefault();
            onOpenMonth(period);
          }}
        >
          <Field id="expense-period" label="Month" help="common.month">
            <MonthPicker id="expense-period" value={period} onChange={setPeriod} max={currentMonth(new Date())} />
          </Field>
          <button type="submit" class="button">
            Open {monthLabel(period)}
          </button>
        </form>
      </Card>
    </>
  );
}

function CustomerBasisCard({ client, orgId, name, hasVerifiedSource }: { client: ApiClient; orgId: string; name: string; hasVerifiedSource: boolean }) {
  const [basis, setBasis] = useState<ExpenseBasis | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => defaultEffectiveMonth(new Date()));
  const [choice, setChoice] = useState<OrgBasisChoice>("fleet");
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<ApiError | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/admin/orgs/{orgId}/expense-basis", { path: { orgId } })
      .then(setBasis)
      .catch((err: unknown) => setError(asApiError(err)))
      .finally(() => setLoading(false));
  };
  useEffect(load, [orgId]);

  const fieldErrors = validateSetBasis({ effectiveMonth: month, basis: orgBasisValue(choice) }, hasVerifiedSource);
  const submit = (event: Event) => {
    event.preventDefault();
    if (Object.keys(fieldErrors).length > 0) return;
    setBusy(true);
    setSaveError(null);
    client
      .put("/v1/admin/orgs/{orgId}/expense-basis", { path: { orgId }, body: { basis: orgBasisValue(choice), effectiveMonth: month } })
      .then(load)
      .catch((err: unknown) => setSaveError(asApiError(err)))
      .finally(() => setBusy(false));
  };

  const now = new Date();
  const inForce = basis ? effectiveBasis(basis, currentMonth(now))?.effectiveMonth ?? null : null;
  const inForceLabel = basis ? basisInForceLabel(basis, now) : null;
  const caption = `${name}'s cost basis`;

  return (
    <Card title={caption} help="finance.customerBasis">
      <p class="card-note">
        In force now: <strong>{inForceLabel ?? "Follows the fleet default"}</strong>.
      </p>
      {error && <ErrorBanner error={error} onRetry={load} />}
      <div class="split-even">
        <DataTable caption={caption} hideCaption columns={basisColumns(inForce)} rows={basis ? basisRows(basis) : []} rowKey={(row) => row.effectiveMonth} loading={loading} emptyMessage="No entries: this customer follows the fleet default." />
        <form class="stack-sm basis-form" onSubmit={submit}>
          <h3>Change {name}'s cost basis</h3>
          <Field id="org-basis-month" label="Effective from" help="finance.effectiveFrom" error={fieldErrors.effectiveMonth}>
            <MonthPicker id="org-basis-month" value={month} onChange={setMonth} min={addMonths(currentMonth(now), -35)} />
          </Field>
          <div class="field">
            <LabelText id="org-basis-kind-label" label="Cost basis" help="common.costBasis" />
            <Segmented options={ORG_BASIS_OPTIONS} value={choice} onChange={(next: OrgBasisChoice) => setChoice(next)} labelledBy="org-basis-kind-label" />
            {fieldErrors.basis && (
              <p class="field-error" id={errorId("org-basis-kind")} role="alert">
                {fieldErrors.basis}
              </p>
            )}
          </div>
          {saveError && <ErrorBanner error={saveError} />}
          <div class="form-actions">
            <button type="submit" class="button button-primary" disabled={busy || Object.keys(fieldErrors).length > 0}>
              {busy ? "Saving…" : "Set cost basis"}
            </button>
          </div>
        </form>
      </div>
    </Card>
  );
}

